import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { fetchCampaignNames, fetchOwnerNames, loadCampaigns } from "../../../api_calls/campaignRequests";
import { Box } from "@mui/material";
import { SpaceBetweenStack } from "../../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { useNavigate } from "react-router-dom";
import { checkUserPermissions, checkUserRole } from "../../../api_calls/userManagement";
import { createCookie, deleteCookie, setBatchLoadingStatus, setFiltersBatchLoadingStatus } from "../../../api_calls/requestConfig";

import FilterPanel from "../../../components/commonComponents/Filters/FilterPanel";

const CampaignListFilters = () => {

  const Campaigns = useSelector((state) => state.userSelections);
  const verifyUserRoleName = checkUserRole();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  const [filterDataLoaded, setFilterDataLoaded] = React.useState(false);

  const [showFilterPanel, setShowFilterPanel] = React.useState(false);

  const loadFiltersData = async () => {
    // load campaign names for campaign name filter
    setFiltersBatchLoadingStatus(true);
    await Promise.all([
      // loadCampaigns(),
      fetchCampaignNames(),
      fetchOwnerNames("list-campaigns")
    ]).then(() => {
      setFiltersBatchLoadingStatus(false);
      setFilterDataLoaded(true);
    });
  }
  // Define filter configuration
  const campaignFiltersConfig = React.useMemo(() => [
    {
      key: 'campaign_name',
      label: 'Campaign Name',
      type: 'multi',
      options: () => Campaigns?.campaign_names_info?.campaign_name_obj,
    },
    {
      key: 'progress',
      label: 'Progress',
      disableSearch: true,
      type: 'multi',
      selectionParam: "label",
      options: () => [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Scheduled' },
        { value: 3, label: 'Completed' },
      ],
    },
    {
      key: 'owner',
      label: 'Owner',
      type: 'multi',
      options: () => Campaigns?.companies_info,
    },
    {
      key: 'shared',
      label: 'Shared',
      type: 'single',
      disableSearch: true,
      selectionParam: "label",
      options: () => [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      enabled: permissionScope.campaign_can_share,
    },
  ], [
    Campaigns?.campaign_names_info,      // update if options data changes
    Campaigns?.companies_info,
    filterDataLoaded,                 // ensure options are reloaded when data is fetched
  ]);

  const navigate = useNavigate();

  const dispatchAction = useDispatch();

  const saveCampaignFilters = (selectedFilters) => {
    const filterValues = {
      // Multi-select drawer filters
      campaign_name: selectedFilters.campaign_name || [],
      // Map the owner drawer filter to the retailer_filter key
      retailer_filter: selectedFilters.owner || [],
      // Map progress drawer filter to duration_filter
      duration_filter: selectedFilters.progress || [],
      // Single-select drawer filter converted to array; empty array if no value
      shared_filter: selectedFilters.shared ? [selectedFilters.shared] : [],
    };

    // If no filters are selected anywhere, clear everything
    const noFiltersSelected =
      (!filterValues.campaign_name || filterValues.campaign_name.length === 0) &&
      (!filterValues.retailer_filter || filterValues.retailer_filter.length === 0) &&
      (!filterValues.assigned_to_filter || filterValues.assigned_to_filter.length === 0) &&
      (!filterValues.status_filter || filterValues.status_filter.length === 0) &&
      (!filterValues.startdate_filter || filterValues.startdate_filter.length === 0) &&
      (!filterValues.enddate_filter || filterValues.enddate_filter.length === 0) &&
      (!filterValues.duration_filter || filterValues.duration_filter.length === 0) &&
      (!filterValues.shared_filter || filterValues.shared_filter.length === 0);
    if (noFiltersSelected) {
      // If nothing is selected, clear existing filters and return
      clearfilters();
      return;
    }
    // Persist filter values to Redux (for PowerPoint export, etc.)
    dispatchAction(
      updateStateData({
        campaign_list_filters: filterValues,
        campaign_list_filter_applied: true,
      })
    );
    // Save filters to cookies for persistence across sessions
    createCookie("campaign_list_filters", JSON.stringify(filterValues));
    // Reload campaigns with filters applied (reset to page 1 but keep page size)
    loadCampaigns(0, Campaigns.campaign_pagination.pageSize);
  };

  const clearfilters = () => {
    // delete filters cookie
    deleteCookie("campaign_list_filters", true);
    // reset campaign listing
    dispatchAction(deleteItems(["campaign_list_filters", "filtered_campaign_list", "campaign_list_filter_applied"]));
    // fetch campaigns again with filters applied
    loadCampaigns(0, Campaigns.campaign_pagination.pageSize);
  };

  // Map Redux state filters to FilterPanel expected keys
  const getMappedInitialFilters = () => {
    if (!Campaigns.campaign_list_filters) {
      return {};
    }

    return {
      campaign_name: Campaigns.campaign_list_filters.campaign_name || [],
      progress: Campaigns.campaign_list_filters.duration_filter || [],
      owner: Campaigns.campaign_list_filters.retailer_filter || [],
      shared: Campaigns.campaign_list_filters.shared_filter?.[0] || null,
    };
  };

  React.useEffect(() => {
    if (typeof Campaigns.campaign_list_filters !== "undefined") {
      // if filters have been applied before, load options data first else filter summary won't be visible at all when we reload page with applied filters
      loadFiltersData().then(() => {
        setShowFilterPanel(true);
      });
    } else {
      // if no filters have been applied yet, we can show the filter panel immediately and load options when user clicks to open it
      setShowFilterPanel(true);
    }
  }, []);

  return (
    <Box>
      {
        showFilterPanel && (
          <SpaceBetweenStack
            direction={"row"}
            spacing={2}
            width={"100%"}
            justifyContent={"space-between"}
            display={"flex"}
          >
            {/* ----------- filter/clearFilter buttons and drawer component ------- */}
            <FilterPanel
              onFilterClick={loadFiltersData}
              filtersConfig={typeof Campaigns.campaign_names_info !== "undefined" ? campaignFiltersConfig : []}
              applyFilterFunc={saveCampaignFilters}
              clearFilterFunc={clearfilters}
              initiallyAppliedVals={getMappedInitialFilters()}
            />
            {/* ------ overview dashboard link (if allowed) ----- */}
            {permissionScope.campaign_dashboard_overview_can_view === true && (
              <>
                {/* ------ overview should be hidden for brand user ----- */}
                {verifyUserRoleName.user_role !== "Brand" && (
                  <ActionButton
                    onClick={(e) => {
                      // e.preventDefault();
                      navigate("/check-campaigns/overview");
                    }}
                    label={"Overview"}
                  />
                )}
              </>
            )}
          </SpaceBetweenStack>
        )
      }
    </Box>
  );
};

export default CampaignListFilters;
